import React from 'react';

import { ArrowBack as ArrowBackIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';

import { DEFAULT_VALUE_IF_NIL } from '@clearverify/constants';
import { getUserFullName } from '@clearverify/utils';

import {
  type EmployeeDataEmploymentRecordInfoFragment,
  type EmployeeDataInfoFragment,
  useGetEmploymentDataForSearchedEmployeeQuery,
} from 'src/api';
import { PositionDetailsContent } from 'src/components/position';
import type { SxProp } from 'src/types/mui';

type EmployeeDataDetailViewProps = {
  entry: EmployeeDataInfoFragment;
  record: EmployeeDataEmploymentRecordInfoFragment;
  employeeSsnEncrypted: string;
  contextLabel?: string;
  backLabel?: string;
  onBack: () => void;
};

export const EmployeeDataDetailView: React.FC<EmployeeDataDetailViewProps> = ({
  entry,
  record,
  employeeSsnEncrypted,
  contextLabel = 'Results',
  backLabel = 'Back to search results',
  onBack,
}) => {
  const employerLocationLabel = [
    record.employerLocation ?? record.employerRegisteredBusinessName ?? DEFAULT_VALUE_IF_NIL,
  ]
    .filter(isNotNil)
    .join(' ');

  const { data, loading } = useGetEmploymentDataForSearchedEmployeeQuery({
    variables: {
      employeeSsnEncrypted,
      companyId: record.companyId,
    },
  });

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={headerStyles}>
        <Stack direction="row" alignItems="center" gap={1} color="text.secondary" flexWrap="wrap">
          <Typography variant="body2">Search</Typography>
          <ChevronRightIcon fontSize="inherit" />
          <Typography variant="body2">{contextLabel}</Typography>
          <ChevronRightIcon fontSize="inherit" />
          <Typography variant="body2" color="text.primary" fontWeight={500}>
            {employerLocationLabel}
          </Typography>
          <ChevronRightIcon fontSize="inherit" />
          <Typography variant="body2" color="text.primary" fontWeight={500}>
            {getUserFullName(entry.user) || record.employerEmployeeId}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}>
            {backLabel}
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={contentStyles}>
        <PositionDetailsContent
          employmentData={data?.getEmploymentDataForSearchedEmployee}
          loading={loading}
          hideDataDispute
        />
      </Paper>
    </Stack>
  );
};

const headerStyles: SxProp = {
  padding: 3,
  borderRadius: '10px',
};

const contentStyles: SxProp = {
  padding: 3,
  borderRadius: '10px',
};
